import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
  type SetupStore,
} from "#src/stores/state-wrapper.ts";
import { type StoreGeneric, type Pinia } from "pinia";
import { type Ref, ref } from "vue";
const DEFAULT_SNACKBAR_TIMEOUT = 10000;
const SNACKBAR_TYPE = {
  SUCCESS: "success",
  ERROR: "error",
};
type SnackbarType = (typeof SNACKBAR_TYPE)[keyof typeof SNACKBAR_TYPE];

interface ShowSnackbarArgs {
  message: string;
  timeout: number;
  color: SnackbarType;
}

interface ShowSnackbarTypeArgs {
  message: string;
  timeout?: number;
}

interface SnackbarState {
  show: boolean;
  message: null | string;
  timeout: number;
  color: null | SnackbarType;
}

interface Snackbar extends SetupStore, ToSetupState<SnackbarState> {
  showSnackbar(args: ShowSnackbarArgs): void;
  showSuccessSnackbar(args: ShowSnackbarTypeArgs): void;
  showErrorSnackbar(args: ShowSnackbarTypeArgs): void;
  hideSnackbar(): void;
}

function initialState(): SnackbarState {
  return {
    show: false,
    message: null,
    timeout: DEFAULT_SNACKBAR_TIMEOUT,
    color: null,
  };
}

function initializeSnackbarStore(): Snackbar {
  const state = generateInitialState<SnackbarState>(initialState());
  function $reset() {
    resetState<SnackbarState>(state, initialState());
  }

  function showSnackbar(args: ShowSnackbarArgs) {
    state.message.value = args.message;
    state.timeout.value = args.timeout;
    state.color.value = args.color;
    state.show.value = true;
  }
  function showSuccessSnackbar(args: ShowSnackbarTypeArgs) {
    showSnackbar({
      message: args.message,
      timeout: args.timeout || DEFAULT_SNACKBAR_TIMEOUT,
      color: SNACKBAR_TYPE.SUCCESS,
    });
  }
  function showErrorSnackbar(args: ShowSnackbarTypeArgs) {
    showSnackbar({
      message: args.message,
      timeout: args.timeout || DEFAULT_SNACKBAR_TIMEOUT,
      color: SNACKBAR_TYPE.ERROR,
    });
  }
  function hideSnackbar() {
    state.show.value = false;
  }

  return {
    $reset,
    ...state,
    showSnackbar,
    showSuccessSnackbar,
    showErrorSnackbar,
    hideSnackbar,
  };
}

export const useSnackbarStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, Snackbar>("snackbar", () => {
    return initializeSnackbarStore();
  })(pinia, hot);
};
